// Does this extension have a mailbox ON THE PBX?
//
// One component, used everywhere an extension is shown, because "is voicemail on?"
// had no answer anywhere in the dashboard. That absence had a cost: the
// cloud-voicemail flip sent `enabled: true` to every extension, and nothing on
// screen would have told an operator that two of Stone Mountain Lodge's were
// cordless handsets with no mailbox on purpose.
//
// Three states, and the third is not cosmetic:
//   on       — the PBX has a mailbox for it
//   off      — it doesn't, and that is a legitimate configuration
//   unknown  — we couldn't ask the PBX. NOT the same as off: acting on "off" when
//              the truth is "don't know" is how voicemail gets switched on or off
//              by accident, so it reads as a question mark, not a state.
//
// Source is the live per-host read (GET /voicemail-status) — see
// hooks/useVoicemailStatus.js. Nothing is cached in Verso: FreePBX owns this, and
// a stored copy drifts the moment someone edits it there.

const STYLES = {
  on:      { cls: 'bg-green-100 text-green-700',  dot: 'bg-green-500', text: 'Voicemail on' },
  off:     { cls: 'bg-gray-100 text-gray-600',    dot: 'bg-gray-400',  text: 'No voicemail' },
  unknown: { cls: 'bg-amber-50 text-amber-800',   dot: 'bg-amber-400', text: 'Voicemail unknown' },
};

const TITLES = {
  on: 'The PBX has a mailbox for this extension',
  off: 'This extension has no mailbox on the PBX — callers can’t leave a message',
  unknown: 'Couldn’t read the mailbox state from the PBX just now',
};

export function voicemailState(entry) {
  if (!entry || entry.enabled == null) return 'unknown';
  return entry.enabled ? 'on' : 'off';
}

/**
 * @param entry  one row from /voicemail-status (`{ enabled, mailbox, absent }`)
 * @param compact  dot + short label, for table cells
 */
export default function VoicemailBadge({ entry, compact = false, className = '' }) {
  const state = voicemailState(entry);
  const s = STYLES[state];
  const title = entry?.absent
    ? 'Verso has this extension but the PBX doesn’t — it hasn’t been provisioned there'
    : TITLES[state];

  if (compact) {
    return (
      <span title={title} className={`inline-flex items-center gap-1.5 text-xs ${className}`}>
        <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${s.dot}`} />
        <span className={state === 'on' ? 'text-gray-700' : 'text-gray-500'}>
          {state === 'on' ? 'on' : state === 'off' ? 'off' : '?'}
        </span>
      </span>
    );
  }

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${s.cls} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.text}
    </span>
  );
}
